import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDragLayer } from 'react-dnd';
import styled from 'styled-components';
import { theme, Typo, Icon, SimpleButton, ConfirmDelete } from '../../../ui';
import { useCourseSections, useCoursePages, useCurrentPageId, useCurrentSectionId, useCurrentCourseId } from '../../viewCourse/hooks';
import { useCourse } from '../../course/hooks';
import { useFeatures } from '../../settings/hooks';
import { getUserThemeColor, getLighterThemeColor, getDarkerThemeColor, shadeColor } from '../../../ui/theme/colors';
import { useReorderPagesMutation, useReorderSectionsMutation, useUpdatePageMutation } from '../editCourseApi';
import { useCourseStructureActions } from '../hooks';
import InlineEdit from './InlineEdit';
import { enqueueSnackbar } from 'notistack';
import { TOAST_ERROR, TOAST_SUCCESS } from '../../../utils/constants';
import { useTranslation } from 'react-i18next';
import { ItemTypes } from '../utils/dragTypes';
import DraggableSection from '../../viewCourse/components/TocMenu/components/DraggableSection';
import DraggablePage from '../../viewCourse/components/TocMenu/components/DraggablePage';

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
`;

const DragHandleBase = styled.div`
  cursor: grab;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
  padding: 0 4px;

  &:active {
    cursor: grabbing;
  }
`;

const CourseTitle = styled.div`
  padding: 5px 5px 5px 15px;
  background: ${({ $lightColor }) => $lightColor || theme.colors.NEU_100};
  margin: 0;
`;

const Header = styled.div`
  padding: 12px 15px;
  margin: 0;
`;

const SectionCard = styled.div`
  background: ${({ $isActive, $ultralightColor }) => ($isActive ? $ultralightColor || theme.colors.NEU_50 : theme.colors.NEU_0)};
  margin: 0;
  overflow: hidden;
  transition: background-color 0.15s;
`;

const SectionHeader = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: ${theme.colors.NEU_0};
  }
`;

const SectionTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const SectionActions = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const PagesList = styled.div`
  padding: 0 0 12px;
  max-height: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '2000px')};
  overflow: hidden;
  opacity: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '1')};
  transition: max-height 0.3s ease, opacity 0.2s ease;
`;

const PageItem = styled.div`
  padding-left: 30px;
  padding-right: 10px;
  padding-top: 8px;
  padding-bottom: 8px;
  margin: 0;
  background: ${({ $isActive, $lightColor }) => ($isActive ? $lightColor || theme.colors.PRIMARY_LIGHT : 'transparent')};
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background-color 0.15s;
  position: relative;
  cursor: pointer;

  ${({ $isActive, $darkColor }) =>
    $isActive &&
    `
    &:before {
      content: '';
      border-left: 6px solid ${$darkColor || theme.colors.PRIMARY};
      position: absolute;
      width: 6px;
      height: 100%;
      left: 0;
      top: 0;
    }
  `}

  &:hover {
    background-color: ${({ $lightColor }) => $lightColor || theme.colors.NEU_100};
  }
`;

const PageTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const PageActions = styled.div`
  display: flex;
  gap: 4px;
`;

const AddButtonWrapper = styled.div`
  padding: 12px 15px;
  display: flex;
  justify-content: space-evenly;
`;

const DragHandle = styled(DragHandleBase)`
  opacity: 1;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: ${theme.colors.WARNING_LIGHT};
`;

const VisualStructureEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const courseSections = useCourseSections();
  const coursePages = useCoursePages();
  const currentPageId = useCurrentPageId();
  const currentSectionId = useCurrentSectionId();
  const courseId = useCurrentCourseId();
  const course = useCourse();
  const features = useFeatures();

  // Detect when a section is being dragged to auto-collapse all sections
  const { isDraggingSection } = useDragLayer((monitor) => ({
    isDraggingSection: monitor.isDragging() && monitor.getItemType() === ItemTypes.SECTION,
  }));

  // API mutations
  const [reorderPages] = useReorderPagesMutation();
  const [updatePage] = useUpdatePageMutation();
  const [reorderSections] = useReorderSectionsMutation();

  // Navigation callback for page creation/duplication
  const handleNavigateToPage = useCallback(
    (pageId) => {
      navigate(`/edit/course/${courseId}/${pageId}`);
    },
    [navigate, courseId],
  );

  // Course structure actions (add, rename, duplicate, delete)
  const structureActions = useCourseStructureActions(courseId, {
    onNavigateToPage: handleNavigateToPage,
    allPages: coursePages,
    allSections: courseSections,
    currentPageId,
  });

  const themeColor = course?.theme?.color;

  const brandColor = useMemo(() => {
    return getUserThemeColor(themeColor);
  }, [themeColor]);

  const lightColor = useMemo(() => {
    return getLighterThemeColor(themeColor);
  }, [themeColor]);

  const ultralightColor = useMemo(() => {
    return shadeColor(themeColor, 0.95);
  }, [themeColor]);

  const darkColor = useMemo(() => {
    return getDarkerThemeColor(themeColor);
  }, [themeColor]);

  // Navigation handler (matching TocMenu pattern for edit context)
  const navigateToPage = useCallback(
    (pageId) => {
      if (pageId && courseId) {
        navigate(`/edit/course/${courseId}/${pageId}`);
      }
    },
    [navigate, courseId],
  );

  // Drag-and-drop handlers for pages
  const handlePageDrop = useCallback(
    async (draggedPageId, targetSectionId, targetIndex) => {
      try {
        const draggedPageObj = coursePages.find((p) => p.id === draggedPageId);
        if (!draggedPageObj) return;

        const sourceSectionId = draggedPageObj.sectionId;

        // If moving within the same section, use reorderPages
        if (sourceSectionId === targetSectionId) {
          const sectionPages = coursePages.filter((p) => p.sectionId === targetSectionId).sort((a, b) => a.order - b.order);

          const currentIndex = sectionPages.findIndex((p) => p.id === draggedPageId);
          if (currentIndex === targetIndex) return; // No change

          // Reorder the pages array
          const reorderedPages = [...sectionPages];
          const [removed] = reorderedPages.splice(currentIndex, 1);

          // Adjust target index when dragging down - after removal, indices shift up by 1
          const adjustedTargetIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
          reorderedPages.splice(adjustedTargetIndex, 0, removed);

          const pageIds = reorderedPages.map((p) => p.id);

          await reorderPages({ sectionId: targetSectionId, pageIds, courseId }).unwrap();
          enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
        } else {
          // Moving to a different section - update page's sectionId
          await updatePage({
            pageId: draggedPageId,
            courseId,
            sectionId: targetSectionId,
            order: targetIndex + 1,
          }).unwrap();
          enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
        }
      } catch (error) {
        console.error('Failed to move page:', error);
        enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      }
    },
    [coursePages, reorderPages, updatePage, courseId, t],
  );

  // Drag-and-drop handlers for sections
  const handleSectionDrop = useCallback(
    async (draggedSectionId, targetIndex) => {
      try {
        const sections = [...courseSections].sort((a, b) => a.order - b.order);
        const currentIndex = sections.findIndex((s) => s.id === draggedSectionId);

        if (currentIndex === targetIndex) return; // No change

        // Reorder sections
        const reorderedSections = [...sections];
        const [removed] = reorderedSections.splice(currentIndex, 1);

        // Adjust target index when dragging down - after removal, indices shift up by 1
        const adjustedTargetIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
        reorderedSections.splice(adjustedTargetIndex, 0, removed);

        // Extract section IDs in new order
        const sectionIds = reorderedSections.map((s) => s.id);

        await reorderSections({ courseId, sectionIds }).unwrap();
        enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      } catch (error) {
        console.error('Failed to reorder sections:', error);
        enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      }
    },
    [courseSections, reorderSections, courseId, t],
  );

  // Section action handlers
  const handleAddSection = useCallback(() => {
    // Get the last section order
    const lastOrder = courseSections.length > 0 ? Math.max(...courseSections.map((s) => s.order)) : -1;
    structureActions.handleAddSection(lastOrder);
  }, [courseSections, structureActions]);

  const handleRenameSection = useCallback(
    (section) => {
      structureActions.openRenameSectionDialog(section);
    },
    [structureActions],
  );

  const handleDuplicateSection = useCallback(
    (sectionId) => {
      structureActions.handleDuplicateSection(sectionId);
    },
    [structureActions],
  );

  const handleDeleteSection = useCallback(
    (section) => {
      structureActions.openDeleteSectionDialog(section);
    },
    [structureActions],
  );

  // Page action handlers
  const handleAddPage = useCallback(
    (sectionId) => {
      const sectionPages = coursePages.filter((p) => p.sectionId === sectionId);
      const lastOrder = sectionPages.length > 0 ? Math.max(...sectionPages.map((p) => p.order)) : -1;
      structureActions.handleAddPage(sectionId, lastOrder);
    },
    [coursePages, structureActions],
  );

  const handleRenamePage = useCallback(
    (page) => {
      structureActions.openRenamePageDialog(page);
    },
    [structureActions],
  );

  const handleDuplicatePage = useCallback(
    (pageId) => {
      structureActions.handleDuplicatePage(pageId);
    },
    [structureActions],
  );

  const handleDeletePage = useCallback(
    (page) => {
      structureActions.openDeletePageDialog(page);
    },
    [structureActions],
  );

  return (
    <Container>
      {/* Course Title */}
      {course?.name && (
        <CourseTitle $lightColor={ultralightColor}>
          <Typo.MediumText fontWeight='600' $textAlign='left'>
            {course.name}
          </Typo.MediumText>
        </CourseTitle>
      )}

      <Header>
        <Typo.MediumText color={theme.colors.NEU_600}>{t('editCourse.structureEditor.description')}</Typo.MediumText>
        <Typo.SmallText color={theme.colors.NEU_600}>{t('editCourse.structureEditor.dragToReorder')}</Typo.SmallText>
      </Header>

      {courseSections?.map((section, sectionIndex) => {
        const sectionPages = coursePages?.filter((page) => page.sectionId === section.id).sort((a, b) => a.order - b.order) || [];
        const isActiveSection = currentSectionId === section.id;
        const isQuiz = section.type === 'quiz';

        return (
          <DraggableSection key={section.id} section={section} index={sectionIndex} onDrop={handleSectionDrop} themeColor={themeColor}>
            <SectionCard $isActive={isActiveSection} $ultralightColor={ultralightColor}>
              <SectionHeader>
                <DragHandle onClick={(e) => e.stopPropagation()}>
                  <Icon.DragIndicator size='small' color={theme.colors.NEU_600} />
                </DragHandle>

                <SectionTitle>
                  <InlineEdit
                    isEditing={structureActions.inlineEditItem?.type === 'section' && structureActions.inlineEditItem?.id === section.id}
                    value={section.name}
                    onSave={(newName) => structureActions.handleRenameSection(section.id, newName)}
                    onCancel={structureActions.cancelInlineEdit}
                    themeColor={brandColor}
                    isLoading={structureActions.isLoading}
                  >
                    <Typo.MediumText fontWeight='500'>{section.name}</Typo.MediumText>
                  </InlineEdit>
                </SectionTitle>

                <SectionActions onClick={(e) => e.stopPropagation()}>
                  <SimpleButton
                    icon='Add'
                    size='x-small'
                    variant='ghost'
                    bgColor={brandColor}
                    onClick={() => handleAddPage(section.id)}
                    aria-label={t('editCourse.addPageAriaLabel')}
                  />
                  <SimpleButton
                    icon='Edit'
                    size='x-small'
                    variant='ghost'
                    bgColor={brandColor}
                    onClick={() => handleRenameSection(section)}
                    aria-label={t('editCourse.renameSectionAriaLabel')}
                  />

                  <SimpleButton
                    icon='Copy'
                    size='x-small'
                    variant='ghost'
                    bgColor={brandColor}
                    onClick={() => handleDuplicateSection(section.id)}
                    aria-label={t('editCourse.duplicateSectionAriaLabel')}
                  />

                  <SimpleButton
                    icon='Delete'
                    size='x-small'
                    bgColor={theme.colors.ACTION}
                    noTooltip
                    variant='ghost'
                    onClick={() => handleDeleteSection(section)}
                    aria-label={t('editCourse.deleteSectionAriaLabel')}
                  />
                </SectionActions>
              </SectionHeader>

              {isQuiz && !features?.quiz && (
                <WarningBanner>
                  <Icon.Info size='x-small' color={theme.colors.WARNING} />
                  <Typo.SmallText color={theme.colors.WARNING}>{t('editCourse.quizFeatureDisabled')}</Typo.SmallText>
                </WarningBanner>
              )}

              <PagesList $isCollapsed={isDraggingSection}>
                {sectionPages.map((page, pageIndex) => {
                  const isActivePage = currentPageId === page.id;

                  return (
                    <DraggablePage
                      key={page.id}
                      page={page}
                      sectionId={section.id}
                      index={pageIndex}
                      onDrop={handlePageDrop}
                      themeColor={themeColor}
                      isQuiz={isQuiz}
                    >
                      <PageItem $isActive={isActivePage} $darkColor={darkColor} $lightColor={lightColor} onClick={() => navigateToPage(page.id)}>
                        <DragHandle onClick={(e) => e.stopPropagation()}>
                          <Icon.DragIndicator size='x-small' color={theme.colors.NEU_600} />
                        </DragHandle>

                        <PageTitle>
                          <InlineEdit
                            isEditing={structureActions.inlineEditItem?.type === 'page' && structureActions.inlineEditItem?.id === page.id}
                            value={page.name}
                            onSave={(newName) => structureActions.handleRenamePage(page.id, newName)}
                            onCancel={structureActions.cancelInlineEdit}
                            themeColor={brandColor}
                            isLoading={structureActions.isLoading}
                          >
                            <Typo.SmallText>{page.name}</Typo.SmallText>
                          </InlineEdit>
                        </PageTitle>

                        <PageActions onClick={(e) => e.stopPropagation()}>
                          <SimpleButton
                            icon='Edit'
                            size='x-small'
                            variant='ghost'
                            bgColor={brandColor}
                            onClick={() => handleRenamePage(page)}
                            aria-label={t('editCourse.renamePageAriaLabel')}
                          />

                          <SimpleButton
                            icon='Copy'
                            size='x-small'
                            variant='ghost'
                            bgColor={brandColor}
                            onClick={() => handleDuplicatePage(page.id)}
                            aria-label={t('editCourse.duplicatePageAriaLabel')}
                          />

                          <SimpleButton
                            icon='Delete'
                            size='x-small'
                            bgColor={theme.colors.ACTION}
                            variant='ghost'
                            noTooltip
                            onClick={() => handleDeletePage(page)}
                            aria-label={t('editCourse.deletePageAriaLabel')}
                          />
                        </PageActions>
                      </PageItem>
                    </DraggablePage>
                  );
                })}
              </PagesList>
            </SectionCard>
          </DraggableSection>
        );
      })}

      <AddButtonWrapper>
        <SimpleButton variant='tertiary' startIcon='Add' size='x-small' bgColor={brandColor} onClick={handleAddSection}>
          {t('editCourse.addSection')}
        </SimpleButton>
        {features?.quiz && (
          <SimpleButton variant='tertiary' startIcon='Add' size='x-small' bgColor={brandColor} onClick={structureActions.handleAddQuiz}>
            {t('editCourse.addQuiz')}
          </SimpleButton>
        )}
      </AddButtonWrapper>

      {/* Delete Confirmation Dialog */}
      <ConfirmDelete
        open={structureActions.deleteDialog.open}
        onClose={structureActions.closeDeleteDialog}
        onAcceptDelete={structureActions.handleDeleteConfirm}
        title={structureActions.deleteDialog.type === 'section' ? t('editCourse.deleteSection') : t('editCourse.deletePage')}
        description={
          structureActions.deleteDialog.type === 'section'
            ? t('editCourse.deleteSectionConfirm', { name: structureActions.deleteDialog.item?.name })
            : t('editCourse.deletePageConfirm', { name: structureActions.deleteDialog.item?.name })
        }
      />
    </Container>
  );
};

export default VisualStructureEditor;
